// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  ContaReceberDrawer.tsx
//  Sheet lateral com detalhe completo de conta a receber + analíticas.
// ════════════════════════════════════════════════════════════════════════════

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react';
import { useDetalheContaReceber } from '../hooks/useDetalheContaReceber';
import { calcularStatus } from '../types';

interface ContaReceberDrawerProps {
  id: number | null;
  onClose: () => void;
}

const brl = (v: number | null | undefined) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmt = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

export function ContaReceberDrawer({ id, onClose }: ContaReceberDrawerProps) {
  const { data: conta, isLoading } = useDetalheContaReceber(id);

  // Só é RECEBIMENTO o que está quitado; o resto são parcelas ainda em aberto.
  const recebimentos    = (conta?.analiticas ?? []).filter((a) => a.isPago);
  const parcelasAbertas = (conta?.analiticas ?? [])
    .filter((a) => !a.isPago)
    .sort((a, b) => a.nroParcela - b.nroParcela);

  // O atraso do título é o da PARCELA em aberto mais antiga — não o do cabeçalho.
  // `conta.dtVencimento` guarda o vencimento da 1ª parcela; num carnê cujas
  // primeiras parcelas já foram pagas, ele fica no passado e o título aparecia
  // como "Atrasado" mesmo com todas as parcelas abertas vencendo no futuro.
  // Sem parcela (título legado órfão) cai no cabeçalho, que é o que existe.
  const vencimentoRelevante = parcelasAbertas.length > 0
    ? parcelasAbertas.reduce(
        (menor, a) => (a.dtVencimento < menor ? a.dtVencimento : menor),
        parcelasAbertas[0].dtVencimento)
    : conta?.dtVencimento;

  const status = conta && vencimentoRelevante
    ? calcularStatus(conta.isPago, vencimentoRelevante)
    : null;

  const statusColor: Record<string, string> = {
    pago:     'bg-green-500/10 text-green-600',
    recebido: 'bg-green-500/10 text-green-600',
    pendente: 'bg-yellow-500/10 text-yellow-600',
    atrasado: 'bg-red-500/10 text-red-600',
  };

  return (
    <Sheet open={id !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle>Detalhe — Conta a Receber #{id}</SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-5 w-full" />)}
          </div>
        ) : !conta ? (
          <p className="text-sm text-muted-foreground">Conta não encontrada.</p>
        ) : (
          <div className="space-y-6">
            {/* ── Cabeçalho ── */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Documento</p>
                <p className="font-semibold">{conta.numDocumento || conta.duplicata || `#${conta.id}`}</p>
              </div>
              <div className="flex gap-2">
                {conta.isCancelado && (
                  <Badge className="bg-gray-500/10 text-gray-600">
                    <XCircle className="h-3 w-3 mr-1" /> Cancelado
                  </Badge>
                )}
                {status && !conta.isCancelado && (
                  <Badge className={statusColor[status]}>
                    {status === 'pago' || status === 'recebido' ? <CheckCircle className="h-3 w-3 mr-1" />
                      : status === 'atrasado' ? <AlertCircle className="h-3 w-3 mr-1" />
                      : <Clock className="h-3 w-3 mr-1" />}
                    {status === 'pago' ? 'Recebido' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </Badge>
                )}
              </div>
            </div>

            {/* ── Dados principais ── */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Cliente</p>
                <p title={`Cliente #${conta.clienteId}`}>
                  {conta.clienteNome || `Cliente #${conta.clienteId}`}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Parcela</p>
                <p>{conta.nroParcela}/{conta.qtdParcelas}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Emissão</p>
                <p>{fmt(conta.dtEmissao)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Vencimento</p>
                <p>{fmt(conta.dtVencimento)}</p>
              </div>
              {conta.dtPagamento && (
                <div>
                  <p className="text-muted-foreground text-xs">Recebimento</p>
                  <p>{fmt(conta.dtPagamento)}</p>
                </div>
              )}
              {conta.diasAtraso > 0 && !conta.isPago && (
                <div>
                  <p className="text-muted-foreground text-xs">Dias em Atraso</p>
                  <p className="text-red-600 font-semibold">{conta.diasAtraso} dias</p>
                </div>
              )}
              {conta.lancamento && (
                <div className="col-span-2">
                  <p className="text-muted-foreground text-xs">Lançamento</p>
                  <p className="truncate">{conta.lancamento}</p>
                </div>
              )}
            </div>

            <Separator />

            {/* ── Valores ── */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor Original</span>
                <span>{brl(conta.valorOriginal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor a Receber</span>
                <span>{brl(conta.valorReceber)}</span>
              </div>
              {conta.valorDesconto > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Desconto</span>
                  <span>– {brl(conta.valorDesconto)}</span>
                </div>
              )}
              {conta.valorAcrescimo > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Acréscimo</span>
                  <span>+ {brl(conta.valorAcrescimo)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-semibold text-orange-600">
                <span>Saldo</span>
                <span>{brl(conta.valorSaldo)}</span>
              </div>
              {conta.isPago && (
                <div className="flex justify-between font-semibold text-green-600">
                  <span>Valor Recebido</span>
                  <span>{brl(conta.valorPago)}</span>
                </div>
              )}
            </div>

            {/* ── Recebimentos x parcelas ainda em aberto ──
                Antes iam juntos em "Histórico de Recebimentos": parcela em aberto
                aparecia como recebimento, com data vazia do legado e valor R$ 0,00. */}
            {recebimentos.length > 0 && (
              <>
                <Separator />
                <div>
                  <h3 className="text-sm font-semibold mb-2">
                    Histórico de Recebimentos ({recebimentos.length})
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Dt. Receb.</TableHead>
                        <TableHead className="text-right">Valor Pago</TableHead>
                        <TableHead className="text-right">Desconto</TableHead>
                        <TableHead>Forma</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recebimentos.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>{fmt(a.dtPagamento)}</TableCell>
                          <TableCell className="text-right text-green-600">{brl(a.valorPago)}</TableCell>
                          <TableCell className="text-right">{brl(a.valorDesconto)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground truncate max-w-[120px]">
                            {a.formaPagamentoLancamento || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}

            {parcelasAbertas.length > 0 && (
              <>
                <Separator />
                <div>
                  <h3 className="text-sm font-semibold mb-2">
                    Parcelas em Aberto ({parcelasAbertas.length})
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Parcela</TableHead>
                        <TableHead>Vencimento</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parcelasAbertas.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>{a.nroParcela}</TableCell>
                          <TableCell>{fmt(a.dtVencimento)}</TableCell>
                          <TableCell className="text-right">{brl(a.valorReceber)}</TableCell>
                          <TableCell className="text-right font-medium">{brl(a.saldo)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
